<script lang="ts">
    // Components
    import NowPlayingQueuePanel from './NowPlayingQueuePanel.svelte';
    import NowPlayingQueueSidebar from './NowPlayingQueueSidebar.svelte';

    // Actions
    import { makeClearQueueAction } from '~/actions/playback-actions';
    import { createEventDispatcher } from 'svelte';

    // Stores
    import { getI18n } from '~/stores/i18n';
    import { queueMetadata } from '~/stores/queue-metadata';
    import { selectedItemIndex } from '~/stores/selected-item-index';

    // Utilities
    import {
        mediaItemToQueueItem,
        type IndexedMusicKitQueueItem,
    } from '~/utils/playback/queue';
    import { getPlayback } from '~/utils/playback';
    import { getJet } from '~/jet';

    // Shared Utilities
    import { debounce } from '@amp/web-app-components/src/utils/debounce';

    // Local Types
    import type { ItemMap, QueueComponentType } from './types';

    /**
     * Used to determine if this is the side drawer or now playing panel
     */
    export let componentType: QueueComponentType;

    /**
     * Used by panel rendering
     */
    export let isMobileView: boolean = false;

    /**
     * Used by sidebar rendering
     */
    export let isOpen: boolean = false;

    /**
     * Element to tell context menu where to render menu options
     * Only used by PlaybackItem in mobile view NowPlayingQueuePanel
     */
    export let parentElement: HTMLElement = null;

    const dispatch = createEventDispatcher();
    const i18n = getI18n();
    const jet = getJet();
    const { queue } = getPlayback();

    // Labels
    const queueHeading = $i18n.t('ASE.Web.MusicClassical.UpNext.Heading');
    const clearButtonLabel = $i18n.t('ASE.Web.MusicClassical.UpNext.Clear');
    const emptyQueueText = $i18n.t('ASE.Web.MusicClassical.UpNext.Empty');

    // Keyboard event consts
    const KEYBOARD_DEBOUNCE_RATE = 250;
    const NAVIGATION_KEYS = {
        UP: 'ArrowUp',
        DOWN: 'ArrowDown',
        LEFT: 'ArrowLeft',
        RIGHT: 'ArrowRight',
    };

    const debounceArrowLeftHandler = debounce(
        arrowLeftHandler,
        KEYBOARD_DEBOUNCE_RATE,
    );
    const debounceArrowRightHandler = debounce(
        arrowRightHandler,
        KEYBOARD_DEBOUNCE_RATE,
    );

    // Queue Items
    $: nowPlayingItemIndex = $queue.nowPlayingItemIndex;
    $: nowPlayingItemId = $queue.nowPlayingItemInfo?.id;
    $: allItems = $queue.items.map(mediaItemMapToQueueItem);

    $: unplayedItems = allItems.slice(nowPlayingItemIndex + 1);
    $: historyItems = allItems.slice(0, nowPlayingItemIndex + 1);

    $: isEmpty = unplayedItems.length === 0;
    $: queueIsPlaying = $queue?.derivedStates?.isPlaying;
    $: queueIsPaused = $queue?.derivedStates?.isPaused;
    $: nowPlayingItem = $queueMetadata.get(nowPlayingItemId);

    // Creates an object that relies on Classical API metadata
    // and is used for presentational purposes, as well as
    // giving us access to Classical-specific item actions
    const mediaItemMapToQueueItem = (
        item: IndexedMusicKitQueueItem,
    ): ItemMap => ({
        ...item,
        item: mediaItemToQueueItem(item.item, item.index),
        uniqueId: `${
            item.item.attributes?.playParams?.catalogId ||
            item.item.attributes?.playParams?.id ||
            item.item.id
        }-${item.index}`,
    });

    // Edit actions
    $: showClearButton = !isEmpty;
    $: clearQueueAction = makeClearQueueAction();

    // Keyboard event handlers
    /* Play previous item */
    async function arrowLeftHandler() {
        if ((!queueIsPaused && !queueIsPlaying) || !nowPlayingItemIndex) {
            return;
        }

        const previousItem = historyItems[historyItems.length - 2];

        if (previousItem) {
            await jet.perform(previousItem.item.playAction);
        }
    }

    /* Play next item */
    async function arrowRightHandler(e) {
        if ((!queueIsPaused && !queueIsPlaying) || nowPlayingItemIndex < 0)
            return;

        const nextItem = unplayedItems[0];

        if (nextItem) {
            await jet.perform(nextItem.item.playAction);

            if ($selectedItemIndex <= nextItem.index) {
                // select first up next item when the selected item gets played and isn't visble in the queue anymore
                selectedItemIndex.set(nextItem.index + 1);
            }
        }
    }

    function handleClearQueue() {
        jet.perform(clearQueueAction);
        selectedItemIndex.set(null);
    }

    async function handleKeyboardEvents(event: CustomEvent<KeyboardEvent>) {
        if (isEmpty || isMobileView) {
            return;
        }

        const { key } = event.detail;
        const minIndex = unplayedItems[0]?.index;
        const maxIndex = allItems.length - 1;

        if (isNaN(minIndex) || isNaN(maxIndex)) {
            return;
        }

        if (
            [
                NAVIGATION_KEYS.UP,
                NAVIGATION_KEYS.DOWN,
                NAVIGATION_KEYS.LEFT,
                NAVIGATION_KEYS.RIGHT,
            ].includes(key)
        ) {
            event.preventDefault();
        }

        switch (key) {
            // Restore now-playing item into queue; also play the item prior.
            case NAVIGATION_KEYS.LEFT:
                debounceArrowLeftHandler();
                break;

            // Play selected if queue is already active.
            case NAVIGATION_KEYS.RIGHT:
                if (!queueIsPaused && !queueIsPlaying) return;
                // Passing undefined because TS linting is entirely convinced
                // that debounceArrowRightHandler requires an argument.
                // The linter is wrong.  Wrong wrong wrong.
                debounceArrowRightHandler(undefined);
                break;

            // Navigate to previous item.
            case NAVIGATION_KEYS.UP:
                selectedItemIndex.set(
                    $selectedItemIndex - 1 < minIndex
                        ? maxIndex
                        : $selectedItemIndex - 1,
                );
                break;

            // Navigate to next item.
            case NAVIGATION_KEYS.DOWN:
                const nextIndex = $selectedItemIndex + 1;

                // There may be cases where the min index is greater than 1
                // so check that we are within the index boundaries
                selectedItemIndex.set(
                    nextIndex > maxIndex || nextIndex < minIndex
                        ? minIndex
                        : nextIndex,
                );
                break;
        }
    }

    // Reset selected item index when opening/closing the queue
    // and forward the event on with the original event value
    function handleToggleOpen(e) {
        dispatch('toggleOpen', e.detail);
        selectedItemIndex.set(null);
    }
</script>

{#if componentType === 'panel'}
    <NowPlayingQueuePanel
        {isMobileView}
        item={nowPlayingItem}
        {isEmpty}
        {clearButtonLabel}
        {emptyQueueText}
        {queueHeading}
        {showClearButton}
        {unplayedItems}
        on:close
        on:play={debounceArrowRightHandler}
        on:clearQueue={handleClearQueue}
        on:keyboardEvent={handleKeyboardEvents}
        bind:parentElement
    />
{:else}
    <NowPlayingQueueSidebar
        {isOpen}
        {isEmpty}
        {clearButtonLabel}
        {emptyQueueText}
        {queueHeading}
        {showClearButton}
        {unplayedItems}
        on:toggleOpen={handleToggleOpen}
        on:play={debounceArrowRightHandler}
        on:clearQueue={handleClearQueue}
        on:keyboardEvent={handleKeyboardEvents}
    />
{/if}
